import type { MobileDatabase } from "../../runtime/ports";
import { offlineAccessLeases } from "@sqlite/schema";
import { eq } from "drizzle-orm";
import type { AuthSession } from "../../runtime/authSession";
import type { RouterOutputs } from "@utils/trpc";
import { evaluateOfflineAccessLease, type OfflineAccessState } from "./state";

type OfflineAccessDecision = RouterOutputs["user"]["getOfflineAccess"];
type RefreshOptions = { retryInvalid?: boolean };

export function createOfflineAccess(deps: {
  db: MobileDatabase;
  auth: Pick<AuthSession, "currentUser" | "store">;
  /** The saved sign-in `useAuth` restores at start-up (its MMKV auth mirror). */
  savedUserId(): string | null;
  api: {
    user: { getOfflineAccess: { query(): Promise<OfflineAccessDecision> } };
  };
  isCurrent(): boolean;
  platform: string;
  now(): number;
}) {
  const { db } = deps;
  const listeners = new Set<() => void>();
  const userWaiters = new Set<() => void>();
  let requestInFlight: {
    userId: string;
    promise: Promise<OfflineAccessDecision>;
  } | null = null;

  const notify = () => listeners.forEach((listener) => listener());
  /** Firebase's own user: the only one a lease can be refreshed for. */
  const currentUserId = () => deps.auth.currentUser()?.uid ?? null;
  /**
   * Whose lease decides offline playback: `null` when signed out, `undefined`
   * while not known yet. At a cold start Firebase keeps `currentUser` null
   * until its start-up reload ends; with an expired ID token on a dead link
   * that takes a minute or more, while `useAuth` already reports the saved
   * sign-in as logged in. Until Firebase reports, that saved user's own lease
   * row decides: it grants nothing the lease itself does not.
   */
  const offlineUserId = (): string | null | undefined => {
    const firebaseUserId = currentUserId();
    if (firebaseUserId) return firebaseUserId;
    // The runtime records Firebase's first report, user or null, here.
    if (deps.auth.store.getState().firebaseUser !== undefined) return null;
    return deps.savedUserId() ?? undefined;
  };

  // When Firebase reports (above all, when it finishes restoring the saved
  // user), whose lease decides can change: re-evaluate every observer.
  const stopAuth = deps.auth.store.subscribe((state, previous) => {
    if (state.firebaseUser === previous.firebaseUser) return;
    for (const wake of [...userWaiters]) wake();
    notify();
  });

  const readLease = (userId: string, database: MobileDatabase = db) => {
    try {
      return database
        .select()
        .from(offlineAccessLeases)
        .where(eq(offlineAccessLeases.userId, userId))
        .get();
    } catch {
      // The first render can precede SQLite migrations. No row means locked;
      // the lifecycle hook validates as soon as migrations finish.
      return undefined;
    }
  };

  const persistInvalidation = (
    userId: string,
    reason: "server_invalid" | "expired" | "clock_rollback",
    now: Date,
    database: MobileDatabase = db,
  ) => {
    database
      .update(offlineAccessLeases)
      .set({
        invalidatedAtDevice: now.toISOString(),
        invalidationReason: reason,
        maxObservedAtDevice: now.toISOString(),
      })
      .where(eq(offlineAccessLeases.userId, userId))
      .run();
    notify();
  };

  const invalidateOfflineAccessFromServer = (
    now = new Date(deps.now()),
    database: MobileDatabase = db,
    userId = currentUserId(),
  ) => {
    if (!deps.isCurrent() || !userId || deps.platform === "web") return;
    persistInvalidation(userId, "server_invalid", now, database);
  };

  /** The user whose lease row decides, or the state when none does. */
  const leaseOwner = (
    userId: string | null | undefined,
  ): string | OfflineAccessState => {
    if (!deps.isCurrent() || deps.platform === "web") {
      return { isValid: false, reason: "missing" };
    }
    if (userId === undefined) return { isValid: false, reason: "auth_pending" };
    return userId || { isValid: false, reason: "missing" };
  };

  /** `userId` defaults to whose lease decides offline playback right now. */
  const getOfflineAccessState = (
    userId: string | null | undefined = offlineUserId(),
    now = new Date(deps.now()),
  ): OfflineAccessState => {
    const owner = leaseOwner(userId);
    if (typeof owner !== "string") return owner;
    return evaluateOfflineAccessLease(readLease(owner), now);
  };

  /**
   * Playback-boundary read. Besides returning the state, it advances the
   * greatest observed clock and permanently records expiry/rollback so changing
   * the wall clock later cannot revive an invalid lease.
   */
  const observeOfflineAccessState = (
    userId: string | null | undefined = offlineUserId(),
    now = new Date(deps.now()),
    database: MobileDatabase = db,
  ): OfflineAccessState => {
    const owner = leaseOwner(userId);
    if (typeof owner !== "string") return owner;
    const lease = readLease(owner, database);
    const state = evaluateOfflineAccessLease(lease, now);

    if (!state.isValid) {
      if (
        lease &&
        !lease.invalidationReason &&
        (state.reason === "server_invalid" ||
          state.reason === "expired" ||
          state.reason === "clock_rollback")
      ) {
        persistInvalidation(owner, state.reason, now, database);
      }
      return state;
    }

    if (
      lease &&
      state.maxObservedAtDevice &&
      state.maxObservedAtDevice !== lease.maxObservedAtDevice
    ) {
      database
        .update(offlineAccessLeases)
        .set({ maxObservedAtDevice: state.maxObservedAtDevice })
        .where(eq(offlineAccessLeases.userId, owner))
        .run();
    }
    return state;
  };

  const observeOfflineAccess = (
    ...args: Parameters<typeof observeOfflineAccessState>
  ) => observeOfflineAccessState(...args).isValid;

  /**
   * Resolves once whose lease decides is known: at once, unless Firebase is
   * still restoring with no saved sign-in. Firebase always reports in the
   * end. Returns false if the session ended while waiting, including when
   * Firebase's report itself ends the account before the caller resumes.
   */
  const waitForUser = () =>
    new Promise<void>((resolve) => {
      if (!deps.isCurrent() || offlineUserId() !== undefined) return resolve();
      const wake = () => {
        userWaiters.delete(wake);
        resolve();
      };
      userWaiters.add(wake);
    }).then(() => deps.isCurrent());

  const applyDecision = (
    userId: string,
    decision: OfflineAccessDecision,
    receivedAtDevice = new Date(deps.now()),
  ) => {
    const receivedAt = receivedAtDevice.toISOString();
    db.insert(offlineAccessLeases)
      .values({
        userId,
        validatedAtServer: decision.validatedAt,
        validUntilServer: decision.validUntil,
        receivedAtDevice: receivedAt,
        maxObservedAtDevice: receivedAt,
        invalidatedAtDevice: decision.isValid ? null : receivedAt,
        invalidationReason: decision.isValid ? null : "server_invalid",
      })
      .onConflictDoUpdate({
        target: offlineAccessLeases.userId,
        set: {
          validatedAtServer: decision.validatedAt,
          validUntilServer: decision.validUntil,
          receivedAtDevice: receivedAt,
          maxObservedAtDevice: receivedAt,
          invalidatedAtDevice: decision.isValid ? null : receivedAt,
          invalidationReason: decision.isValid ? null : "server_invalid",
          updatedAt: receivedAt,
        },
      })
      .run();
    notify();
  };

  const requestDecision = (userId: string) => {
    if (requestInFlight?.userId === userId) return requestInFlight.promise;

    const promise = deps.api.user.getOfflineAccess.query().finally(() => {
      if (requestInFlight?.promise === promise) {
        requestInFlight = null;
      }
    });
    requestInFlight = { userId, promise };
    return promise;
  };

  const delay = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const refreshOfflineAccess = async (
    options: RefreshOptions = {},
  ): Promise<OfflineAccessDecision> => {
    const userId = currentUserId();
    if (!userId)
      throw new Error("Cannot validate offline access without a user");

    const delays = options.retryInvalid
      ? [0, 500, 1_500, 3_000]
      : [0, 500, 1_500];
    let lastError: unknown;

    for (let index = 0; index < delays.length; index += 1) {
      if (!deps.isCurrent())
        throw new Error("Offline access session has ended");
      if (delays[index] > 0) await delay(delays[index]);
      try {
        const decision = await requestDecision(userId);
        if (!deps.isCurrent() || currentUserId() !== userId) {
          throw new Error("Offline access validation user changed");
        }
        const isFinalAttempt = index === delays.length - 1;
        if (decision.isValid || !options.retryInvalid || isFinalAttempt) {
          applyDecision(userId, decision);
          return decision;
        }
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError ?? new Error("Offline access validation failed");
  };

  const subscribeToOfflineAccess = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return {
    getOfflineAccessState,
    observeOfflineAccess,
    observeOfflineAccessState,
    waitForUser,
    refreshOfflineAccess,
    invalidateOfflineAccessFromServer,
    subscribeToOfflineAccess,
    dispose: () => {
      stopAuth();
      listeners.clear();
      for (const wake of [...userWaiters]) wake();
    },
  };
}

export { evaluateOfflineAccessLease } from "./state";
